import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import NumericInput from '@/components/NumericInput';
import { customer360Service } from '@/data/customer360Service';
import type { VoucherTemplateOption } from '@/types/Customer360.interface';

const backendMessage = (error: unknown): string | null => {
  const message = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  return typeof message === 'string' ? message : null;
};

interface ActionDialogProps {
  open: boolean;
  onClose: () => void;
  /** After a successful action (refresh the card). */
  onDone: (message: string) => void;
  userId: number;
  branchId: number;
  branchName: string;
  customerName: string;
}

/** "Puan ekle": manual points with an optional reason (shown in history). */
export const AddPointsDialog = ({
  open,
  onClose,
  onDone,
  userId,
  branchId,
  branchName,
  customerName
}: ActionDialogProps) => {
  const { t } = useTranslation();
  const [points, setPoints] = useState(0);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPoints(0);
      setReason('');
      setError(null);
    }
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await customer360Service.addPoints(branchId, {
        userId,
        points,
        reason: reason.trim() || undefined
      });
      onDone(t('customerCard.points.added', { n: points }));
      onClose();
    } catch (err) {
      setError(backendMessage(err) ?? t('customerCard.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('customerCard.points.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('customerCard.points.hint', {
            customer: customerName,
            branch: branchName
          })}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <NumericInput
          fullWidth
          label={t('customerCard.points.amount')}
          value={points}
          onChange={setPoints}
          min={1}
          max={100000}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label={t('customerCard.points.reason')}
          value={reason}
          onChange={(event) => setReason(event.target.value.slice(0, 200))}
          placeholder={t('customerCard.points.reasonPlaceholder')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || points < 1}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {t('customerCard.points.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** "Kupon hediye et": one of the branch's active voucher templates. */
export const GrantVoucherDialog = ({
  open,
  onClose,
  onDone,
  userId,
  branchId,
  branchName,
  customerName
}: ActionDialogProps) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<VoucherTemplateOption[] | null>(
    null
  );
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTemplateId('');
    setError(null);
    setTemplates(null);
    let cancelled = false;
    customer360Service
      .getVoucherTemplates(branchId)
      .then((items) => {
        if (!cancelled) setTemplates(items);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, branchId]);

  const handleSave = async () => {
    if (templateId === '') return;
    setSaving(true);
    setError(null);
    try {
      const { code } = await customer360Service.grantVoucher(branchId, {
        userId,
        voucherTemplateId: templateId
      });
      onDone(t('customerCard.voucher.granted', { code: code ?? '' }));
      onClose();
    } catch (err) {
      setError(backendMessage(err) ?? t('customerCard.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('customerCard.voucher.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('customerCard.voucher.hint', {
            customer: customerName,
            branch: branchName
          })}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {templates === null ? (
          <CircularProgress size={24} />
        ) : templates.length === 0 ? (
          <Alert severity="info">{t('customerCard.voucher.none')}</Alert>
        ) : (
          <TextField
            select
            fullWidth
            label={t('customerCard.voucher.template')}
            value={templateId}
            onChange={(event) => setTemplateId(Number(event.target.value))}
          >
            {templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || templateId === ''}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {t('customerCard.voucher.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
